/**
 * Company Info & Bank Accounts settings page
 * Stores data in configuration.config JSONB under "company" and "bank_accounts" keys.
 */
import { Save, Plus, Trash2 } from "lucide-react";
import { useNotify } from "ra-core";
import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { getSupabaseClient } from "../providers/supabase/supabase";

interface CompanyInfo {
  legal_name: string;
  commercial_name: string;
  registration_number: string;
  vat_number: string;
  address_line1: string;
  address_line2: string;
  city: string;
  state: string;
  zipcode: string;
  country: string;
  email: string;
  phone: string;
  website: string;
  logo_url: string;
}

interface BankAccount {
  currency: string;
  bank_name: string;
  account_name: string;
  account_number: string;
  swift_code: string;
  bank_address: string;
  routing_number: string;
  iban: string;
}

const EMPTY_COMPANY: CompanyInfo = {
  legal_name: "",
  commercial_name: "",
  registration_number: "",
  vat_number: "",
  address_line1: "",
  address_line2: "",
  city: "",
  state: "",
  zipcode: "",
  country: "China",
  email: "",
  phone: "",
  website: "",
  logo_url: "",
};

const EMPTY_BANK: BankAccount = {
  currency: "USD",
  bank_name: "",
  account_name: "",
  account_number: "",
  swift_code: "",
  bank_address: "",
  routing_number: "",
  iban: "",
};

export const CompanySettingsPage = () => {
  const notify = useNotify();
  const [company, setCompany] = useState<CompanyInfo>({ ...EMPTY_COMPANY });
  const [banks, setBanks] = useState<BankAccount[]>([{ ...EMPTY_BANK }]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    getSupabaseClient()
      .from("configuration")
      .select("config")
      .eq("id", 1)
      .maybeSingle()
      .then(({ data }) => {
        const cfg = (data?.config as Record<string, unknown>) ?? {};
        if (cfg.company) setCompany(cfg.company as CompanyInfo);
        if (Array.isArray(cfg.bank_accounts) && cfg.bank_accounts.length > 0) {
          setBanks(cfg.bank_accounts as BankAccount[]);
        }
        setLoading(false);
      });
  }, []);

  const handleSave = async () => {
    setSaving(true);
    // Merge into existing config
    const { data: existing } = await getSupabaseClient()
      .from("configuration")
      .select("config")
      .eq("id", 1)
      .maybeSingle();

    const existingConfig = (existing?.config as Record<string, unknown>) ?? {};
    const { error } = await getSupabaseClient()
      .from("configuration")
      .update({
        config: {
          ...existingConfig,
          company,
          bank_accounts: banks,
        },
      })
      .eq("id", 1);

    if (error) {
      notify("Failed to save settings", { type: "error" });
    } else {
      notify("Company settings saved", { type: "success" });
    }
    setSaving(false);
  };

  const updateCompany = (field: keyof CompanyInfo, value: string) => {
    setCompany((prev) => ({ ...prev, [field]: value }));
  };

  const updateBank = (
    index: number,
    field: keyof BankAccount,
    value: string,
  ) => {
    setBanks((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  const addBank = () => setBanks((prev) => [...prev, { ...EMPTY_BANK }]);
  const removeBank = (index: number) =>
    setBanks((prev) => prev.filter((_, i) => i !== index));

  if (loading)
    return <div className="p-8 text-muted-foreground">Loading...</div>;

  return (
    <div className="max-w-3xl mx-auto p-6 space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold">Company & Trade Settings</h1>
        <Button onClick={handleSave} disabled={saving}>
          <Save className="w-4 h-4 mr-2" />
          {saving ? "Saving..." : "Save"}
        </Button>
      </div>

      {/* Company Info */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Company Information</CardTitle>
          <p className="text-sm text-muted-foreground">
            Used as the seller header on PI, CI, and other trade documents.
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <Field
              label="Legal Name"
              value={company.legal_name}
              onChange={(v) => updateCompany("legal_name", v)}
              placeholder="ShieldPort Trading Co., Ltd."
            />
            <Field
              label="Commercial Name"
              value={company.commercial_name}
              onChange={(v) => updateCompany("commercial_name", v)}
              placeholder="ShieldPort"
            />
            <Field
              label="Registration Number"
              value={company.registration_number}
              onChange={(v) => updateCompany("registration_number", v)}
            />
            <Field
              label="VAT / Tax Number"
              value={company.vat_number}
              onChange={(v) => updateCompany("vat_number", v)}
            />
          </div>
          <Separator />
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <Field
                label="Address Line 1"
                value={company.address_line1}
                onChange={(v) => updateCompany("address_line1", v)}
              />
            </div>
            <div className="col-span-2">
              <Field
                label="Address Line 2"
                value={company.address_line2}
                onChange={(v) => updateCompany("address_line2", v)}
              />
            </div>
            <Field
              label="City"
              value={company.city}
              onChange={(v) => updateCompany("city", v)}
            />
            <Field
              label="State / Province"
              value={company.state}
              onChange={(v) => updateCompany("state", v)}
            />
            <Field
              label="Zip / Postal Code"
              value={company.zipcode}
              onChange={(v) => updateCompany("zipcode", v)}
            />
            <Field
              label="Country"
              value={company.country}
              onChange={(v) => updateCompany("country", v)}
            />
          </div>
          <Separator />
          <div className="grid grid-cols-2 gap-4">
            <Field
              label="Email"
              value={company.email}
              onChange={(v) => updateCompany("email", v)}
              type="email"
            />
            <Field
              label="Phone"
              value={company.phone}
              onChange={(v) => updateCompany("phone", v)}
            />
            <Field
              label="Website"
              value={company.website}
              onChange={(v) => updateCompany("website", v)}
            />
            <Field
              label="Logo URL"
              value={company.logo_url}
              onChange={(v) => updateCompany("logo_url", v)}
              placeholder="https://..."
            />
          </div>
        </CardContent>
      </Card>

      {/* Bank Accounts */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-base">Bank Accounts</CardTitle>
              <p className="text-sm text-muted-foreground">
                Printed on PI for wire transfer. Add one per currency.
              </p>
            </div>
            <Button size="sm" variant="outline" onClick={addBank}>
              <Plus className="w-3 h-3 mr-1" /> Add Account
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          {banks.map((bank, i) => (
            <div key={i} className="space-y-3">
              {i > 0 && <Separator />}
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">Account {i + 1}</span>
                {banks.length > 1 && (
                  <button
                    onClick={() => removeBank(i)}
                    className="text-muted-foreground hover:text-destructive"
                  >
                    <Trash2 className="w-3 h-3" />
                  </button>
                )}
              </div>
              <div className="grid grid-cols-2 gap-4">
                <Field
                  label="Currency"
                  value={bank.currency}
                  onChange={(v) => updateBank(i, "currency", v)}
                  placeholder="USD"
                />
                <Field
                  label="Bank Name"
                  value={bank.bank_name}
                  onChange={(v) => updateBank(i, "bank_name", v)}
                />
                <Field
                  label="Account Name"
                  value={bank.account_name}
                  onChange={(v) => updateBank(i, "account_name", v)}
                />
                <Field
                  label="Account Number"
                  value={bank.account_number}
                  onChange={(v) => updateBank(i, "account_number", v)}
                />
                <Field
                  label="SWIFT / BIC Code"
                  value={bank.swift_code}
                  onChange={(v) => updateBank(i, "swift_code", v)}
                />
                <Field
                  label="IBAN (if applicable)"
                  value={bank.iban}
                  onChange={(v) => updateBank(i, "iban", v)}
                />
                <Field
                  label="Routing Number (US)"
                  value={bank.routing_number}
                  onChange={(v) => updateBank(i, "routing_number", v)}
                />
                <div className="col-span-2">
                  <Field
                    label="Bank Address"
                    value={bank.bank_address}
                    onChange={(v) => updateBank(i, "bank_address", v)}
                  />
                </div>
              </div>
            </div>
          ))}
        </CardContent>
      </Card>

      <div className="flex justify-end">
        <Button onClick={handleSave} disabled={saving}>
          <Save className="w-4 h-4 mr-2" />
          {saving ? "Saving..." : "Save Settings"}
        </Button>
      </div>
    </div>
  );
};

CompanySettingsPage.path = "/company-settings";

const Field = ({
  label,
  value,
  onChange,
  placeholder,
  type = "text",
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  type?: string;
}) => (
  <div>
    <Label className="text-xs">{label}</Label>
    <Input
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="mt-1 h-8 text-xs"
    />
  </div>
);
